import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "../api/axios";
import SEO from "../seo/SEO";
import config from "../config/config";

const OrderConfirmation = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const orderUuid = searchParams.get("order_uuid");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orderStatus, setOrderStatus] = useState(null);

  useEffect(() => {
    const verifyOrder = async () => {
      if (orderUuid) {
        // ✅ Already have order_uuid, fetch order details
        try {
          const response = await axios.get(`/api/orders/verify?order_uuid=${orderUuid}`);
          if (response.data.success) {
            navigate(`/order-success/${orderUuid}`, { replace: true });
          } else {
            setOrderStatus({ error: "Order verification failed. Please contact support." });
          }
        } catch (error) {
          console.error("🔥 Error verifying order:", error);
          setOrderStatus({ error: "An unexpected error occurred while verifying your order. Please try again later." });
        } finally {
          setLoading(false);
        }
      } else if (sessionId) {
        // ✅ Fetch `order_uuid` first
        try {
          const response = await axios.get(`/api/orders/verify?session_id=${sessionId}`);

          if (response.data.success) {
            navigate(`/order-success/${response.data.order_uuid}`, { replace: true });
          } else {
            // ✅ If order is missing, assume it was due to stock running out
            setOrderStatus({
              error: "Your order could not be completed. This may be because the item(s) went out of stock before checkout was completed.",
              notCharged: true,
            });
          }
        } catch (error) {
          console.error("🔥 Error fetching order UUID:", error);

          // ✅ Ensure stock issue message is shown when needed
          if (error.response?.status === 404) {
            setOrderStatus({
              error: "Your order could not be completed. This may be because the item(s) went out of stock before checkout was completed.",
              notCharged: true,
            });
          } else {
            setOrderStatus({ error: "An unexpected error occurred while verifying your order. Please try again later.", notCharged: true });
          }
        } finally {
          setLoading(false);
        }
      } else {
        setOrderStatus({
          error: "Missing order details. Please try again.",
          notCharged: true,
        });
        setLoading(false);
      }
    };

    verifyOrder();
  }, [sessionId, orderUuid, navigate]);

  if (loading)
    return (
      <div className="order-success">
        <div className="spinner"></div>
        <p>Verifying your order...</p>
      </div>
    );

  if (orderStatus?.error)
    return (
    <>

      <SEO
          robots="noindex, nofollow"
          title="Order Confirmation - Withrow Knives"
          description="Your order confirmation is being processed. Please wait while we complete your purchase."
          url={`${config.metadata.siteUrl}/order-confirmation`}
      />
      
      <div className="order-success">
        <h2>Order Not Completed</h2>
        <p>{orderStatus.error}</p>
        {orderStatus.notCharged && (
          <p style={{fontWeight: "bold" }}>You have NOT been charged.</p>
        )}
        <Link to="/shop-knives/all-knives" className="continue-shopping-btn" aria-label="Continue shopping for more products">
          Back to Shop
        </Link>
      </div>
    </>
    );

  return null; // ✅ No UI needed if we successfully redirected to order success
};

export default OrderConfirmation;